import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageShell from '../components/PageShell';
import ProductCard from '../components/ProductCard';
import CustomerNav from '../components/CustomerNav';
import request from '../api';
import { formatDateTime } from '../dateUtils';
import { formatMXN } from '../numberUtils';

const mapsUrl = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
const whatsappUrl = (phone) => `https://wa.me/${phone.replace(/\D/g, '')}`;

function MyWins() {
    const [token] = useState(localStorage.getItem('customerToken'));
    const [wins, setWins] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/auctions');
            return;
        }
        request('/customer/wins', { token })
            .then(setWins)
            .finally(() => setLoading(false));
    }, [token, navigate]);

    return (
        <PageShell title="Mis Pujas Ganadoras" nav={<CustomerNav />}>
            {loading && <Typography color="text.secondary">Cargando...</Typography>}

            {!loading && wins.length > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    ¡Felicidades! Visita cada tienda en persona para reclamar tu artículo y pagar el
                    monto de tu puja ganadora.
                </Alert>
            )}

            <Grid container spacing={3}>
                {wins.map((item) => (
                    <Grid key={item.id} item xs={12} sm={6} md={4}>
                        <ProductCard
                            image={item.image_url}
                            title={item.title}
                            subtitle={`Vendido por ${item.store_name}`}
                            storeLogoUrl={item.store_logo_url}
                            storeName={item.store_name}
                            meta={
                                <>
                                    <Chip
                                        size="small"
                                        icon={<EmojiEventsIcon />}
                                        label={`Ganado por ${formatMXN(item.winning_bid)}`}
                                        color="primary"
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                                        {formatDateTime(item.settled_at)}
                                    </Typography>
                                    {item.status === 'delivered' ? (
                                        <Chip size="small" color="success" label="Entregado" sx={{ mt: 0.5 }} />
                                    ) : (
                                        <>
                                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Visita a {item.store_name} para reclamar y pagar. Muestra este código:
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 700, letterSpacing: '0.3em', color: 'primary.main' }}
                                            >
                                                {item.redemption_code}
                                            </Typography>
                                            <Accordion
                                                disableGutters
                                                square
                                                sx={{
                                                    mt: 2,
                                                    borderRadius: '12px',
                                                    bgcolor: 'action.hover',
                                                    backgroundImage: 'none',
                                                    '&:before': { display: 'none' },
                                                    '&.Mui-expanded': { margin: 0, mt: 2 },
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    sx={{
                                                        px: 2,
                                                        '&.Mui-expanded': { minHeight: 48 },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                        sx={{ fontWeight: 700 }}
                                                    >
                                                        <StorefrontIcon fontSize="small" />
                                                        Información de tienda
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                                                    <Box display="flex" alignItems="center" gap={1.25} sx={{ mb: 1.5 }}>
                                                        <Avatar
                                                            src={item.store_logo_url}
                                                            alt={item.store_name}
                                                            sx={{ width: 32, height: 32 }}
                                                        >
                                                            {item.store_name?.[0]}
                                                        </Avatar>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.store_name}
                                                        </Typography>
                                                    </Box>
                                                    {item.store_address && (
                                                        <Typography
                                                            variant="body2"
                                                            display="flex"
                                                            alignItems="flex-start"
                                                            gap={1}
                                                            color="text.secondary"
                                                            sx={{ mb: 1 }}
                                                        >
                                                            <LocationOnIcon fontSize="small" sx={{ mt: '2px' }} />
                                                            <Link
                                                                href={mapsUrl(item.store_address)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                color="inherit"
                                                                underline="hover"
                                                            >
                                                                {item.store_address}
                                                            </Link>
                                                        </Typography>
                                                    )}
                                                    {item.store_phone && (
                                                        <Typography
                                                            variant="body2"
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                            color="text.secondary"
                                                            sx={{ mb: item.store_delivery_location ? 1 : 0 }}
                                                        >
                                                            <PhoneIcon fontSize="small" />
                                                            <Link
                                                                href={whatsappUrl(item.store_phone)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                color="inherit"
                                                                underline="hover"
                                                            >
                                                                {item.store_phone}
                                                            </Link>
                                                        </Typography>
                                                    )}
                                                    {item.store_delivery_location && (
                                                        <Typography
                                                            variant="body2"
                                                            display="flex"
                                                            alignItems="flex-start"
                                                            gap={1}
                                                            color="text.secondary"
                                                        >
                                                            <LocalShippingIcon fontSize="small" sx={{ mt: '2px' }} />
                                                            <span>
                                                                <strong>Lugar de entrega:</strong> {item.store_delivery_location}
                                                            </span>
                                                        </Typography>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </>
                                    )}
                                </>
                            }
                        />
                    </Grid>
                ))}
                {!loading && wins.length === 0 && (
                    <Grid item xs={12}>
                        <Typography color="text.secondary" align="center">
                            Aún no has ganado ninguna subasta.
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </PageShell>
    );
}

export default MyWins;
